"use client";

import React, { useMemo, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { blogPosts, isOriginalDeepDive } from "@/data/blogPosts";
import Icon from "@/components/ui/Icon";
import Image from "@/components/ui/Image";

const POSTS_PER_PAGE = 12;

const getLinkText = (category: string) => {
  if (category === "Market Trends") return "View Analysis";
  if (category === "Tech Comparison") return "Read Comparison";
  if (category === "Systems Design") return "Read Architecture";
  if (category === "Performance") return "Read Case Study";
  return "Read Article";
};

// Fallback background image asset when a post has no explicit image
const getPostBgImage = (index: number) => {
  const assetNumber = ((index * 3 + 1) % 24) + 1;
  return `/images/general/asset-${assetNumber}.webp`;
};

// Generate pagination numbers array with ellipses
function getPaginationItems(current: number, total: number): (number | "...")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items: (number | "...")[] = [];

  if (current <= 4) {
    for (let i = 1; i <= 5; i++) items.push(i);
    items.push("...");
    items.push(total);
  } else if (current >= total - 3) {
    items.push(1);
    items.push("...");
    for (let i = total - 4; i <= total; i++) items.push(i);
  } else {
    items.push(1);
    items.push("...");
    items.push(current - 1);
    items.push(current);
    items.push(current + 1);
    items.push("...");
    items.push(total);
  }

  return items;
}

export default function EditorsPicks() {
  const searchParams = useSearchParams();
  const prefersReducedMotion = useReducedMotion();
  const [searchQuery, setSearchQuery] = useState("");

  // Extract unique categories and counts
  const categories = useMemo(
    () => ["All", ...Array.from(new Set(blogPosts.map((p) => p.category)))],
    []
  );

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: blogPosts.length };
    for (const post of blogPosts) {
      counts[post.category] = (counts[post.category] || 0) + 1;
    }
    return counts;
  }, []);

  // Read URL query parameters
  const categoryParam = searchParams.get("category");
  const selectedCategory =
    categoryParam && categories.includes(categoryParam) ? categoryParam : "All";

  // Filter posts based on active category + instant search query, prioritizing Original Deep Dives first
  const filteredPosts = useMemo(() => {
    const byCategory =
      selectedCategory === "All"
        ? blogPosts
        : blogPosts.filter((p) => p.category === selectedCategory);

    const trimmedQuery = searchQuery.trim().toLowerCase();
    const matched = !trimmedQuery
      ? byCategory
      : byCategory.filter((p) => {
          const haystack = [
            p.title,
            p.excerpt,
            p.category,
            ...(p.keywords || []),
          ]
            .join(" ")
            .toLowerCase();
          return haystack.includes(trimmedQuery);
        });

    // Prioritize full-length original deep dives ahead of legacy archive stubs
    const deepDives = matched.filter(isOriginalDeepDive);
    const stubs = matched.filter((p) => !isOriginalDeepDive(p));
    return [...deepDives, ...stubs];
  }, [selectedCategory, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));

  const pageParam = searchParams.get("page");
  const parsedPage = pageParam ? parseInt(pageParam, 10) : 1;
  const rawPage = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const currentPage = searchQuery.trim() ? 1 : Math.min(rawPage, totalPages);

  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const visiblePosts = filteredPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);
  const endIndex = Math.min(startIndex + POSTS_PER_PAGE, filteredPosts.length);

  // Build canonical & clean URLs preserving parameters
  const buildPageUrl = (targetPage: number, targetCategory = selectedCategory) => {
    const params = new URLSearchParams();
    if (targetCategory && targetCategory !== "All") {
      params.set("category", targetCategory);
    }
    if (targetPage > 1) {
      params.set("page", targetPage.toString());
    }
    const qs = params.toString();
    return qs ? `/blog?${qs}` : "/blog";
  };

  const handlePageNavigation = () => {
    if (typeof window !== "undefined") {
      const el = document.getElementById("articles-grid");
      if (el) {
        const topOffset = el.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top: Math.max(0, topOffset), behavior: "smooth" });
      }
    }
  };

  const paginationItems = getPaginationItems(currentPage, totalPages);

  // Smooth scroll with animation ONLY when returning via an explicit #post- hash (e.g. from Back to Blog)
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const scrollToTargetCard = () => {
      const hash = window.location.hash;
      if (!hash || !hash.startsWith("#post-")) {
        return;
      }

      const targetSlug = hash.replace("#post-", "");
      if (targetSlug) {
        const targetEl = document.getElementById(`post-${targetSlug}`);
        if (targetEl) {
          setTimeout(() => {
            const navOffset = 110;
            const top = targetEl.getBoundingClientRect().top + window.pageYOffset - navOffset;
            window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });

            targetEl.classList.add(
              "ring-4",
              "ring-primary",
              "ring-offset-4",
              "ring-offset-background",
              "shadow-2xl"
            );
            setTimeout(() => {
              targetEl.classList.remove(
                "ring-4",
                "ring-primary",
                "ring-offset-4",
                "ring-offset-background",
                "shadow-2xl"
              );
              if (window.history && window.history.replaceState) {
                const cleanUrl = window.location.pathname + window.location.search;
                window.history.replaceState(null, "", cleanUrl);
              }
            }, 2500);
          }, 120);
        }
      }
    };

    scrollToTargetCard();
    window.addEventListener("hashchange", scrollToTargetCard);
    return () => window.removeEventListener("hashchange", scrollToTargetCard);
  }, [currentPage]);

  return (
    <section className="pt-4 sm:pt-8 md:pt-14 pb-section-gap px-margin-mobile md:px-gutter max-w-container-max mx-auto">
      {/* Precision scroll anchor placed inside section right above the badge */}
      <div id="articles-grid" className="scroll-mt-24 pointer-events-none" />

      {/* Enterprise Header Section */}
      <div className="mb-10 pb-8 border-b border-outline-variant/30">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            {/* Architectural Eyebrow Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 mb-4">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span>Architectural Knowledge Base</span>
            </div>

            <h2 className="font-headline-lg-mobile text-headline-lg-mobile md:text-headline-lg md:font-headline-lg font-semibold leading-[1.12] tracking-[-0.02em] text-on-surface">
              <span className="heading-glitter">Engineering Insights &amp; Articles</span>
            </h2>

            <p className="font-body-lg text-body-lg mt-4 leading-relaxed text-on-surface-variant max-w-xl">
              Deep dives on systems architecture, AI agent backends, Next.js, and high-frequency infrastructure.
            </p>
          </div>

          {/* Right-Side Telemetry Metric Box */}
          <div className="flex items-center gap-3 shrink-0 self-start lg:self-end">
            <div className="px-5 py-3.5 rounded-2xl bg-surface-container-lowest/80 border border-outline-variant/30 shadow-xs backdrop-blur-sm flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                <Icon name="terminal" size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-extrabold text-on-surface tracking-tight">
                    {filteredPosts.length}
                  </span>
                  <span className="text-xs font-semibold text-on-surface-variant">
                    {filteredPosts.length === 1 ? "Article" : "Articles"}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_#10B981]" />
                </div>
                <p className="text-[11px] text-on-surface-variant/80 font-medium">
                  {selectedCategory === "All"
                    ? "Production-grade research"
                    : `Filtered by ${selectedCategory}`}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Enterprise Filter Bar + Instant Search Bar */}
        <div className="mt-8 p-3 sm:p-4 rounded-2xl bg-surface-container-lowest/80 border border-outline-variant/30 shadow-xs backdrop-blur-sm flex flex-col gap-3.5">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            {/* Topic Pills Track */}
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((cat) => {
                const isSelected = selectedCategory === cat;
                const count = categoryCounts[cat] || 0;
                return (
                  <Link
                    key={cat}
                    href={buildPageUrl(1, cat)}
                    scroll={false}
                    onClick={handlePageNavigation}
                    className={`group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                      isSelected
                        ? "bg-primary text-white shadow-md shadow-primary/20 ring-1 ring-primary/40"
                        : "bg-surface-container/60 text-on-surface-variant hover:bg-surface-container hover:text-on-surface border border-outline-variant/30"
                    }`}
                    aria-current={isSelected ? "page" : undefined}
                  >
                    <span>{cat}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-md font-mono transition-colors ${
                        isSelected
                          ? "bg-white/20 text-white font-bold"
                          : "bg-surface-container-high/60 text-on-surface-variant/70 group-hover:text-on-surface"
                      }`}
                    >
                      {count}
                    </span>
                  </Link>
                );
              })}
            </div>

            {/* Instant Search Input */}
            <div className="relative w-full md:w-72 lg:w-80 shrink-0">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search architectures, AI, cloud..."
                aria-label="Search blog articles by keyword or technology"
                className="w-full pl-3.5 pr-9 py-2 rounded-xl bg-surface-container/70 border border-outline-variant/40 focus:border-primary focus:outline-none text-xs sm:text-sm text-on-surface placeholder:text-on-surface-variant/60 transition-colors"
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search query"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface p-1 cursor-pointer"
                >
                  <Icon name="close" size={14} />
                </button>
              ) : (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant/50 pointer-events-none">
                  <Icon name="terminal" size={14} />
                </span>
              )}
            </div>
          </div>

          {/* Active Filter Clear Action */}
          {(selectedCategory !== "All" || searchQuery.trim() !== "") && (
            <div className="flex items-center justify-between pt-2 border-t border-outline-variant/20 text-xs">
              <span className="text-on-surface-variant font-medium">
                Showing {filteredPosts.length} matching {filteredPosts.length === 1 ? "article" : "articles"}
                {searchQuery.trim() ? ` for "${searchQuery.trim()}"` : ""}
              </span>
              <Link
                href="/blog"
                scroll={false}
                onClick={() => {
                  setSearchQuery("");
                  handlePageNavigation();
                }}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:text-primary/80 px-2.5 py-1 rounded-lg hover:bg-primary/5 transition-all"
                aria-label="Clear active filters"
              >
                <span>Reset filters</span>
                <Icon name="close" size={14} />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Grid of Posts */}
      {visiblePosts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {visiblePosts.map((pick, index) => {
              const linkText = getLinkText(pick.category);
              const bgImage = pick.image || getPostBgImage(startIndex + index);
              const isDeepDive = isOriginalDeepDive(pick);

              return (
                <motion.div
                  key={`${currentPage}-${pick.slug}`}
                  id={`post-${pick.slug}`}
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.3, delay: (index % 12) * 0.03 }}
                  className="h-full scroll-mt-28 rounded-r-2xl"
                >
                  <Link
                    href={`/blog/${pick.slug}`}
                    onClick={() => {
                      if (typeof window !== "undefined") {
                        sessionStorage.setItem("last_viewed_blog", pick.slug);
                      }
                    }}
                    aria-label={`${linkText}: ${pick.title}`}
                    className="group relative flex flex-col h-full bg-surface-container-lowest border-l-4 border-primary p-7 sm:p-8 rounded-r-2xl shadow-sm border border-outline-variant/30 justify-between overflow-hidden transition-colors duration-300 hover:border-primary/60 hover:shadow-xl cursor-pointer block"
                  >
                    {/* Background Image Reveal on Hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0 pointer-events-none">
                      <Image
                        src={bgImage}
                        alt={pick.title}
                        className="w-full h-full"
                        imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        optimizeWidth={600}
                      />
                      {/* Rich Dark Gradient Overlay for optimal text readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/85 to-black/70" />
                    </div>

                    {/* Card Content Stack */}
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-primary/10 group-hover:bg-primary/25 border border-primary/20 group-hover:border-primary/50 flex items-center justify-center shadow-xs transition-colors duration-300">
                          <Icon
                            name={pick.icon}
                            size={30}
                            className="text-primary group-hover:text-primary-fixed-dim transition-colors"
                          />
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {isDeepDive && (
                            <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-mono font-bold uppercase tracking-wider">
                              Deep Dive
                            </span>
                          )}
                          <span className="text-xs font-bold text-on-surface-variant/70 group-hover:text-slate-300 transition-colors">
                            {pick.readTime}
                          </span>
                        </div>
                      </div>

                      <span className="text-label-md text-[11px] font-label-md text-primary group-hover:text-primary-fixed-dim mb-2 font-bold uppercase tracking-wider block transition-colors">
                        {pick.category}
                      </span>
                      <h3 className="font-headline-md text-xl sm:text-[22px] text-on-surface group-hover:text-white mb-3 font-bold transition-colors leading-tight">
                        {pick.title}
                      </h3>
                      <p className="font-body-md text-sm text-on-surface-variant group-hover:text-slate-200 mb-7 leading-relaxed line-clamp-3 transition-colors">
                        {pick.excerpt}
                      </p>
                    </div>

                    <div className="relative z-10 mt-auto pt-2 flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 font-bold text-primary group-hover:text-primary-fixed-dim group-hover:gap-3 transition-all text-sm">
                        <span>{linkText}</span>
                        <Icon
                          name="arrow_forward"
                          size={16}
                          className="transition-transform group-hover:translate-x-1"
                          aria-hidden="true"
                        />
                      </span>
                      <span className="text-[11px] font-medium text-on-surface-variant/60 group-hover:text-slate-300 transition-colors">
                        {pick.date}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      ) : (
        <div className="py-20 text-center flex flex-col items-center justify-center gap-4">
          <p className="text-on-surface-variant text-base">
            No articles found matching your filter or search query.
          </p>
          <Link
            href="/blog"
            scroll={false}
            onClick={() => {
              setSearchQuery("");
              handlePageNavigation();
            }}
            className="px-5 py-2.5 rounded-xl bg-primary text-white font-bold text-sm"
          >
            View All Articles
          </Link>
        </div>
      )}

      {/* Pagination Section */}
      <nav
        aria-label="Blog pagination navigation"
        className="mt-14 flex flex-col items-center justify-center gap-5 border-t border-outline-variant/30 pt-10"
      >
        {/* Counter Summary */}
        <p className="text-xs font-bold text-on-surface-variant tracking-wide flex items-center gap-1.5">
          <span>Showing</span>
          <span className="text-primary font-extrabold">
            {filteredPosts.length > 0 ? startIndex + 1 : 0}–{endIndex}
          </span>
          <span>of</span>
          <span className="text-on-surface font-extrabold">{filteredPosts.length}</span>
          <span>articles</span>
          <span className="text-on-surface-variant/70 ml-1">
            (Page {currentPage} of {totalPages})
          </span>
        </p>

        {/* Progress Bar Indicator */}
        <div className="w-full max-w-xs h-1.5 rounded-full bg-outline-variant/30 overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500 rounded-full"
            style={{ width: `${(currentPage / totalPages) * 100}%` }}
          />
        </div>

        {/* Numbered Pagination with Anchor Tags - Always in a Single Clean Inline Row */}
        {totalPages > 1 && !searchQuery.trim() && (
          <div className="mt-4 flex items-center justify-center gap-1 sm:gap-2 flex-nowrap max-w-full">
            {/* Previous Page Link */}
            {currentPage > 1 ? (
              <Link
                href={buildPageUrl(currentPage - 1)}
                scroll={false}
                onClick={handlePageNavigation}
                rel="prev"
                aria-label="Go to previous page"
                className="inline-flex items-center justify-center w-8 h-8 sm:w-auto sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm bg-surface-container/70 text-on-surface hover:bg-surface-container hover:text-primary border border-outline-variant/40 hover:border-primary/50 transition-all cursor-pointer shadow-xs active:scale-95 shrink-0"
              >
                <Icon name="chevron_left" size={16} />
                <span className="hidden sm:inline">Previous</span>
              </Link>
            ) : (
              <span
                aria-disabled="true"
                className="inline-flex items-center justify-center w-8 h-8 sm:w-auto sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm bg-surface-container/30 text-on-surface-variant/40 border border-outline-variant/20 cursor-not-allowed select-none shrink-0"
              >
                <Icon name="chevron_left" size={16} />
                <span className="hidden sm:inline">Previous</span>
              </span>
            )}

            {/* Page Number Anchor Links */}
            <div className="flex items-center gap-1 sm:gap-1.5 flex-nowrap">
              {paginationItems.map((item, idx) => {
                if (item === "...") {
                  return (
                    <span
                      key={`ellipsis-${idx}`}
                      className="w-6 h-8 sm:w-9 sm:h-10 flex items-center justify-center text-on-surface-variant/60 font-bold text-xs sm:text-sm select-none shrink-0"
                    >
                      …
                    </span>
                  );
                }

                const isCurrent = item === currentPage;
                const pageUrl = buildPageUrl(item);

                return (
                  <Link
                    key={item}
                    href={pageUrl}
                    scroll={false}
                    onClick={handlePageNavigation}
                    aria-label={`Page ${item}`}
                    aria-current={isCurrent ? "page" : undefined}
                    className={`w-7.5 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center transition-all cursor-pointer shrink-0 ${
                      isCurrent
                        ? "bg-primary text-white shadow-md shadow-primary/25 font-extrabold cursor-default"
                        : "bg-surface-container/70 text-on-surface hover:bg-surface-container hover:text-primary border border-outline-variant/40 hover:border-primary/50 active:scale-95"
                    }`}
                  >
                    {item}
                  </Link>
                );
              })}
            </div>

            {/* Next Page Link */}
            {currentPage < totalPages ? (
              <Link
                href={buildPageUrl(currentPage + 1)}
                scroll={false}
                onClick={handlePageNavigation}
                rel="next"
                aria-label="Go to next page"
                className="inline-flex items-center justify-center w-8 h-8 sm:w-auto sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm bg-surface-container/70 text-on-surface hover:bg-surface-container hover:text-primary border border-outline-variant/40 hover:border-primary/50 transition-all cursor-pointer shadow-xs active:scale-95 shrink-0"
              >
                <span className="hidden sm:inline">Next</span>
                <Icon name="chevron_right" size={16} />
              </Link>
            ) : (
              <span
                aria-disabled="true"
                className="inline-flex items-center justify-center w-8 h-8 sm:w-auto sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm bg-surface-container/30 text-on-surface-variant/40 border border-outline-variant/20 cursor-not-allowed select-none shrink-0"
              >
                <span className="hidden sm:inline">Next</span>
                <Icon name="chevron_right" size={16} />
              </span>
            )}
          </div>
        )}
      </nav>
    </section>
  );
}
